// Custom report builder. Pick a data source, optional date range, group-by + aggregate,
// preview results inline, then Save as a reusable custom report.
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { ReportResultView, type ReportResult } from "@/components/report-result";
import { useAuthStore } from "@/stores/auth-store";

type Source = "meetings" | "tasks" | "deals" | "leads" | "contacts" | "accounts";

interface FieldOption { value: string; label: string }

const SOURCES: { value: Source; label: string; dateField: string; groupFields: FieldOption[]; sumFields: FieldOption[] }[] = [
  {
    value: "meetings", label: "Meetings", dateField: "scheduledAt",
    groupFields: [
      { value: "userId",    label: "Host (User)" },
      { value: "venue",     label: "Venue" },
      { value: "location",  label: "Locality" },
      { value: "accountId", label: "Account" },
      { value: "contactId", label: "Contact" },
    ],
    sumFields: [],
  },
  {
    value: "tasks", label: "Tasks", dateField: "scheduledAt",
    groupFields: [
      { value: "userId",   label: "Owner" },
      { value: "status",   label: "Status" },
      { value: "priority", label: "Priority" },
    ],
    sumFields: [],
  },
  {
    value: "deals", label: "Deals", dateField: "createdAt",
    groupFields: [
      { value: "stage",         label: "Stage" },
      { value: "pipelineType",  label: "Pipeline" },
      { value: "ownerId",       label: "Owner" },
      { value: "accountId",     label: "Account" },
      { value: "leadSource",    label: "Lead source" },
      { value: "entity",        label: "Entity" },
    ],
    sumFields: [
      { value: "valueUsd", label: "Value (USD)" },
    ],
  },
  {
    value: "leads", label: "Leads", dateField: "createdAt",
    groupFields: [
      { value: "status",        label: "Status" },
      { value: "source",        label: "Source" },
      { value: "ownerId",       label: "Owner" },
      { value: "leadIndustry",  label: "Industry" },
      { value: "leadGeography", label: "Geography" },
      { value: "leadDegree",    label: "Degree" },
    ],
    sumFields: [
      { value: "annualRevenue", label: "Annual revenue" },
    ],
  },
  {
    value: "contacts", label: "Contacts", dateField: "createdAt",
    groupFields: [
      { value: "ownerId",           label: "Owner" },
      { value: "accountId",         label: "Account" },
      { value: "leadSource",        label: "Lead source" },
      { value: "contactGeography",  label: "Geography" },
      { value: "communicationPref", label: "Preferred channel" },
    ],
    sumFields: [],
  },
  {
    value: "accounts", label: "Accounts", dateField: "createdAt",
    groupFields: [
      { value: "type",      label: "Type" },
      { value: "entity",    label: "Entity" },
      { value: "industry",  label: "Industry" },
      { value: "rating",    label: "Rating" },
      { value: "ownerId",   label: "Owner" },
      { value: "geography", label: "Geography" },
    ],
    sumFields: [
      { value: "annualRevenue", label: "Annual revenue" },
      { value: "employeeCount", label: "Employees" },
    ],
  },
];

interface Draft {
  name: string;
  description: string;
  category: string;
  source: Source;
  groupBy: string;
  aggregate: "count" | "sum";
  aggregateField: string;
  dateField: string;
  dateFrom: string;
  dateTo: string;
  limit: number;
  isShared: boolean;
}

const EMPTY: Draft = {
  name: "", description: "", category: "", source: "meetings",
  groupBy: "userId", aggregate: "count", aggregateField: "",
  dateField: "scheduledAt", dateFrom: "", dateTo: "",
  limit: 50, isShared: false,
};

export default function ReportBuilderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.me);
  const isEdit = !!id;
  const isAdmin = me?.role === "SUPER_ADMIN" || me?.role === "ADMIN";

  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [preview, setPreview] = useState<ReportResult | null>(null);
  const [previewErr, setPreviewErr] = useState<string | null>(null);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  const existing = useQuery({
    queryKey: ["/reports/custom", id],
    queryFn: async () => (await api.get(`/reports/custom/${id}`)).data,
    enabled: isEdit,
  });

  useEffect(() => {
    if (existing.data) {
      const d = existing.data.definition;
      setDraft({
        name: existing.data.name,
        description: existing.data.description ?? "",
        category: existing.data.category ?? "",
        source: d.source,
        groupBy: d.groupBy ?? "",
        aggregate: d.aggregate ?? "count",
        aggregateField: d.aggregateField ?? "",
        dateField: d.dateField ?? "createdAt",
        dateFrom: d.dateFrom ? new Date(d.dateFrom).toISOString().slice(0, 10) : "",
        dateTo:   d.dateTo   ? new Date(d.dateTo).toISOString().slice(0, 10) : "",
        limit: d.limit ?? 50,
        isShared: existing.data.isShared ?? false,
      });
    }
  }, [existing.data]);

  const sourceMeta = SOURCES.find((s) => s.value === draft.source)!;

  function set<K extends keyof Draft>(k: K, v: Draft[K]) {
    setDraft((d) => ({ ...d, [k]: v }));
  }

  function switchSource(v: Source) {
    const meta = SOURCES.find((s) => s.value === v)!;
    setDraft((d) => ({
      ...d,
      source: v,
      groupBy: meta.groupFields[0]?.value ?? "",
      aggregate: meta.sumFields.length ? d.aggregate : "count",
      aggregateField: meta.sumFields[0]?.value ?? "",
      dateField: meta.dateField,
    }));
  }

  function buildDefinition() {
    return {
      source: draft.source,
      groupBy: draft.groupBy || undefined,
      aggregate: draft.aggregate,
      aggregateField: draft.aggregate === "sum" ? draft.aggregateField : undefined,
      dateField: (draft.dateFrom || draft.dateTo) ? draft.dateField : undefined,
      dateFrom: draft.dateFrom || undefined,
      dateTo: draft.dateTo || undefined,
      limit: draft.limit,
    };
  }

  const runMut = useMutation({
    mutationFn: async () => (await api.post("/reports/custom/run", buildDefinition())).data,
    onSuccess: (r) => { setPreview(r.result); setPreviewErr(null); },
    onError: (err: any) => setPreviewErr(err?.response?.data?.message ?? "Preview failed."),
  });

  const saveMut = useMutation({
    mutationFn: async () => {
      const payload = {
        name: draft.name,
        description: draft.description || undefined,
        category: draft.category || undefined,
        isShared: draft.isShared,
        definition: buildDefinition(),
      };
      if (isEdit) return (await api.put(`/reports/custom/${id}`, payload)).data;
      return (await api.post("/reports/custom", payload)).data;
    },
    onSuccess: (saved) => {
      qc.invalidateQueries({ queryKey: ["/reports/custom"] });
      navigate(`/reports/${saved.id}`);
    },
    onError: (err: any) => setSaveErr(err?.response?.data?.message ?? "Save failed."),
  });

  return (
    <>
      <PageHeader
        title={isEdit ? "Edit Report" : "New Report"}
        description="Pick a data source, define filters and grouping, preview, then save."
      />
      <div className="grid gap-4 p-6 lg:grid-cols-[400px_1fr]">
        <Card>
          <CardHeader><CardTitle className="text-base">Definition</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label htmlFor="rn">Name</Label>
              <Input id="rn" value={draft.name} onChange={(e) => set("name", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="rc">Category</Label>
              <Input id="rc" placeholder="Meetings, Deals, Leads…" value={draft.category} onChange={(e) => set("category", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="rd">Description</Label>
              <Input id="rd" value={draft.description} onChange={(e) => set("description", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="rs">Data source</Label>
              <Select id="rs" value={draft.source} onChange={(e) => switchSource(e.target.value as Source)}>
                {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </Select>
            </div>
            <div>
              <Label htmlFor="rgb">Group by</Label>
              <Select id="rgb" value={draft.groupBy} onChange={(e) => set("groupBy", e.target.value)}>
                <option value="">— No grouping (list records) —</option>
                {sourceMeta.groupFields.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </Select>
            </div>
            <div>
              <Label htmlFor="ragg">Aggregate</Label>
              <Select id="ragg" value={draft.aggregate} onChange={(e) => set("aggregate", e.target.value as "count" | "sum")}>
                <option value="count">Count</option>
                {sourceMeta.sumFields.length > 0 && <option value="sum">Sum</option>}
              </Select>
            </div>
            {draft.aggregate === "sum" && (
              <div>
                <Label htmlFor="raf">Sum field</Label>
                <Select id="raf" value={draft.aggregateField} onChange={(e) => set("aggregateField", e.target.value)}>
                  {sourceMeta.sumFields.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="rf">Date from</Label>
                <Input id="rf" type="date" value={draft.dateFrom} onChange={(e) => set("dateFrom", e.target.value)} />
              </div>
              <div>
                <Label htmlFor="rt">Date to</Label>
                <Input id="rt" type="date" value={draft.dateTo} onChange={(e) => set("dateTo", e.target.value)} />
              </div>
            </div>
            <div>
              <Label htmlFor="rl">Row limit</Label>
              <Input id="rl" type="number" min="1" max="1000" value={draft.limit} onChange={(e) => set("limit", Number(e.target.value) || 50)} />
            </div>
            <div className="flex items-center gap-2">
              <input
                id="rsh"
                type="checkbox"
                checked={draft.isShared}
                onChange={(e) => set("isShared", e.target.checked)}
                disabled={!isAdmin}
              />
              <Label htmlFor="rsh">
                Share with org{!isAdmin && <span className="ml-1 text-xs text-muted-foreground">(admin only)</span>}
              </Label>
            </div>

            {previewErr && <p className="text-sm text-destructive">{previewErr}</p>}
            {saveErr && <p className="text-sm text-destructive">{saveErr}</p>}

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => runMut.mutate()} disabled={runMut.isPending}>
                {runMut.isPending ? "Running…" : "Preview"}
              </Button>
              <Button onClick={() => saveMut.mutate()} disabled={!draft.name || saveMut.isPending}>
                {saveMut.isPending ? "Saving…" : "Save"}
              </Button>
              <Button variant="outline" onClick={() => navigate("/reports")}>Cancel</Button>
            </div>
          </CardContent>
        </Card>

        <div>
          {!preview && (
            <Card>
              <CardContent className="py-16 text-center text-sm text-muted-foreground">
                Click <span className="font-medium">Preview</span> to see the result here.
              </CardContent>
            </Card>
          )}
          {preview && <ReportResultView result={preview} />}
        </div>
      </div>
    </>
  );
}
